import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { UserPlus, Users as UsersIcon } from 'lucide-react';
import {
  useToast, useConfirm,
  PageHeader, StatusBadge, Button, Input, EmptyState,
} from '../components/ui';

const ROLES = [
  { value: 'super_admin', label: 'Super Admin' },
  { value: 'admin',       label: 'Admin' },
  { value: 'manager',     label: 'Manager' },
  { value: 'chef',        label: 'Chef' },
  { value: 'cashier',     label: 'Cashier' },
  { value: 'waiter',      label: 'Waiter' },
  { value: 'rider',       label: 'Rider' },
  { value: 'staff',       label: 'Staff' },
];

const roleVariant = (role) => {
  switch (role) {
    case 'super_admin': return 'danger';
    case 'admin':       return 'primary';
    case 'manager':     return 'warning';
    case 'chef':        return 'success';
    default:            return 'neutral';
  }
};

const roleLabel = (role) =>
  String(role || '').replace('_', ' ');

const UserManagement = () => {
  const { toast } = useToast();
  const confirm = useConfirm();
  const [users, setUsers] = useState([]);
  const [outlets, setOutlets] = useState([]);
  const [form, setForm] = useState({ name: '', email: '', password: '', role: 'staff', outlet: '' });
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    fetchUsers();
    fetchOutlets();
  }, []);

  const fetchUsers = async () => {
    try {
      // includeInactive=true — this is the account management screen, so
      // previously-deactivated users need to stay visible (and reactivatable)
      // here rather than disappearing permanently.
      const res = await axios.get('/api/users?includeInactive=true', { headers: { 'x-auth-token': localStorage.getItem('token') } });
      setUsers(res.data);
    } catch (err) { console.error(err); }
  };

  const fetchOutlets = async () => {
    try {
      const res = await axios.get('/api/outlets', { headers: { 'x-auth-token': localStorage.getItem('token') } });
      setOutlets(res.data);
    } catch (err) { console.error(err); }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      await axios.post('/api/auth/register', form, { headers: { 'x-auth-token': localStorage.getItem('token') } });
      setForm({ name: '', email: '', password: '', role: 'staff', outlet: '' });
      fetchUsers();
      toast.success('User created successfully');
    } catch (err) {
      const data = err.response?.data;
      // validatePassword on the backend returns a detailed errors[] array on
      // top of the generic msg — surface those specifics instead of just
      // "Password validation failed".
      const detail = Array.isArray(data?.errors) ? data.errors.join(' ') : null;
      toast.error(detail || data?.msg || err.message || 'Error creating user');
    } finally {
      setSubmitting(false);
    }
  };

  const handleDelete = async (id) => {
    const ok = await confirm({
      title: 'Deactivate user?',
      message: 'The user will be deactivated and unable to log in.',
      variant: 'danger',
      confirmText: 'Deactivate'
    });
    if (!ok) return;
    try {
      await axios.delete(`/api/users/${id}`, { headers: { 'x-auth-token': localStorage.getItem('token') } });
      toast.success('User deactivated');
      fetchUsers();
    } catch (err) {
      toast.error(err.response?.data?.msg || 'Error deactivating user');
    }
  };

  const handleReactivate = async (id) => {
    try {
      await axios.put(`/api/users/${id}`, { isActive: true }, { headers: { 'x-auth-token': localStorage.getItem('token') } });
      toast.success('User reactivated');
      fetchUsers();
    } catch (err) {
      toast.error(err.response?.data?.msg || 'Error reactivating user');
    }
  };

  return (
    <>
      <PageHeader
        title="User Management"
        subtitle="Create staff accounts and assign roles and outlets."
      />

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Users table */}
        <div className="lg:col-span-2 bg-white rounded-lg border border-neutral-200 shadow-card overflow-hidden">
          <div className="px-5 py-3 border-b border-neutral-200 flex items-center justify-between">
            <h2 className="text-sm font-semibold text-neutral-800">All users</h2>
            <span className="text-xs text-neutral-500">
              {users.length} user{users.length === 1 ? '' : 's'}
            </span>
          </div>
          <div className="overflow-x-auto">
            <table className="app-table">
              <thead>
                <tr>
                  <th>Name</th>
                  <th>Email</th>
                  <th>Role</th>
                  <th>Status</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {users.length === 0 && (
                  <tr>
                    <td colSpan="5">
                      <EmptyState
                        icon={UsersIcon}
                        title="No users yet"
                        message="Create your first user with the form on the right."
                      />
                    </td>
                  </tr>
                )}
                {users.map(user => (
                  <tr key={user._id}>
                    <td className="font-medium">{user.name}</td>
                    <td className="text-neutral-600">{user.email}</td>
                    <td>
                      <StatusBadge variant={roleVariant(user.role)} dot>
                        {roleLabel(user.role)}
                      </StatusBadge>
                    </td>
                    <td>
                      <StatusBadge variant={user.isActive ? 'success' : 'neutral'} dot>
                        {user.isActive ? 'Active' : 'Inactive'}
                      </StatusBadge>
                    </td>
                    <td>
                      {user.isActive ? (
                        <Button size="sm" variant="ghost" onClick={() => handleDelete(user._id)}>
                          Deactivate
                        </Button>
                      ) : (
                        <Button size="sm" variant="secondary" onClick={() => handleReactivate(user._id)}>
                          Reactivate
                        </Button>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>

        {/* Create form */}
        <div className="bg-white rounded-lg border border-neutral-200 shadow-card overflow-hidden h-fit">
          <div className="px-5 py-3 border-b border-neutral-200 flex items-center gap-2">
            <UserPlus size={16} className="text-primary-600" />
            <h2 className="text-sm font-semibold text-neutral-800">Create new user</h2>
          </div>
          <form onSubmit={handleSubmit} className="p-5 space-y-3">
            <Input
              label="Name"
              type="text"
              placeholder="Full name"
              value={form.name}
              onChange={(e) => setForm({ ...form, name: e.target.value })}
              required
            />
            <Input
              label="Email"
              type="email"
              placeholder="you@restaurant.com"
              value={form.email}
              onChange={(e) => setForm({ ...form, email: e.target.value })}
              required
            />
            <Input
              label="Password"
              type="password"
              placeholder="Min 8 chars, upper+lower case, a number"
              value={form.password}
              onChange={(e) => setForm({ ...form, password: e.target.value })}
              required
            />
            <div>
              <label className="text-sm font-medium text-neutral-700 block mb-1">Role</label>
              <select
                value={form.role}
                onChange={(e) => setForm({ ...form, role: e.target.value })}
                className="w-full border border-neutral-300 rounded-md bg-white px-3 py-2 text-sm focus:outline-none focus:ring-4 focus:ring-primary-200 focus:border-primary-500"
              >
                {ROLES.map(r => (
                  <option key={r.value} value={r.value}>{r.label}</option>
                ))}
              </select>
            </div>
            <div>
              <label className="text-sm font-medium text-neutral-700 block mb-1">Outlet (optional)</label>
              <select
                value={form.outlet}
                onChange={(e) => setForm({ ...form, outlet: e.target.value })}
                className="w-full border border-neutral-300 rounded-md bg-white px-3 py-2 text-sm focus:outline-none focus:ring-4 focus:ring-primary-200 focus:border-primary-500"
              >
                <option value="">No outlet</option>
                {outlets.map(outlet => (
                  <option key={outlet._id} value={outlet._id}>{outlet.name}</option>
                ))}
              </select>
            </div>
            <div className="pt-1">
              <Button type="submit" variant="primary" fullWidth loading={submitting}>
                Create user
              </Button>
            </div>
          </form>
        </div>
      </div>
    </>
  );
};

export default UserManagement;
